import { type AxiosRequestConfig } from "axios";
import isEmpty from "lodash/isEmpty";
import { camelCase, upperFirst } from "lodash-es";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { bus } from "shared/boot/emitter";
import { getLocaleText } from "shared/boot/i18n";
import { type MentionDetailWithTvEyesOptions } from "shared/components/MentionDetail/MentionDetailWithTvEyes/types";
import { isBroadcastProviderUnsupported } from "shared/helpers/broadcastProvider";
import { isTveyesExpired } from "shared/helpers/date";
import { compoundId } from "shared/helpers/mentions";
import ModalService from "shared/modals/ModalService";
import type {
  MediaRequest as MediaRequestResource,
  RadioClip as RadioClipResource,
  TvCaption as TvCaptionResource,
} from "shared/resources";
import {
  fetchCurrentUserMediaRequests,
  isMediaRequestCompleted,
  isMediaRequestProcessed,
  isTvEyesMediaRequest,
  mapFromResource,
} from "shared/services/mediaRequest";
import { type Nullable, type Stream } from "shared/types";
import type {
  BasicMediaRequest,
  MediaRequest,
  Mention,
  MentionType,
  RadioClip,
  RadioClipFromResource,
  TvCaption,
  TvCaptionFromResource,
  TvEyesMention,
} from "shared/types/mentions";
import {
  isKinetiqMention,
  isRadioClip,
  isTvCaption,
  isTvEyesMention,
} from "shared/types/mentions/guards";

const MEDIA_REQUEST_POLLING_INTERVAL_MS = 10_000;
const MEDIA_REQUEST_POLLING_MAX_TIMES = 10;

async function fetchMention(
  mention: Mention,
  params: object = {}
): Promise<Mention> {
  const options: AxiosRequestConfig = {};

  if (!isEmpty(params)) {
    options.params = params;
  }

  const response = await $streemApiV1.get(
    `mentions/${compoundId(mention)}`,
    options
  );

  return response.data as Mention;
}

function isTvCaptionOrRadioClip(
  mention: Mention
): mention is TvCaption | RadioClip {
  return isTvCaption(mention) || isRadioClip(mention);
}

function isTvEyesContent(mention: Mention): boolean {
  return isTvCaptionOrRadioClip(mention) && isTvEyesMention(mention);
}

function getBroadcastMediaRequest(
  mention: Mention
): Nullable<BasicMediaRequest> {
  if (!isTvCaptionOrRadioClip(mention)) return null;

  return (
    mention.broadcast_media_request ?? mention.tveyes_media_request ?? null
  );
}

function hasArchivedBroadcastContent(mention: Mention): boolean {
  const mediaRequest = getBroadcastMediaRequest(mention);

  if (isBroadcastProviderUnsupported(mention, mediaRequest)) return false;

  return Boolean(
    mediaRequest?.status === "Completed" && mediaRequest.media_url
  );
}

function hasBroadcastArchiveRequest(mention: Mention): boolean {
  const mediaRequest = getBroadcastMediaRequest(mention);

  if (isBroadcastProviderUnsupported(mention, mediaRequest)) return false;

  return (
    mediaRequest?.status === "In Progress" ||
    hasArchivedBroadcastContent(mention)
  );
}

function hasArchivedTvEyesContent(mention: Mention): boolean {
  return !isKinetiqMention(mention) && hasArchivedBroadcastContent(mention);
}

export type OpenTvEyesMentionModalOptions = MentionDetailWithTvEyesOptions & {
  trackTvEyesMediaRequestStatus?: boolean;
};

function openViewMentionDetailWithTvEyesModal(
  tveyesMention: TvEyesMention,
  options: OpenTvEyesMentionModalOptions = {}
) {
  const { trackTvEyesMediaRequestStatus, ...tvEyesDetailProps } = options;

  ModalService.open("ViewMentionDetailModal", {
    events: {
      close: async () => {
        if (
          isTvCaptionOrRadioClip(tveyesMention) &&
          !isBroadcastProviderUnsupported(tveyesMention) &&
          trackTvEyesMediaRequestStatus
        ) {
          await pollTvEyesMentionMediaRequest(tveyesMention);
        }
      },
    },
    props: {
      mention: tveyesMention,
      tvEyesDetailProps,
    },
  });
}

async function openViewMentionDetailWithKinetiqModal(
  kinetiqMention: TvCaption | RadioClip,
  selectedMediaRequest: BasicMediaRequest,
  stream?: Stream
): Promise<void> {
  try {
    const loadedMention = await fetchMention(kinetiqMention);

    ModalService.open("ViewMentionDetailModal", {
      props: {
        mention: {
          ...loadedMention,
          broadcast_media_request: selectedMediaRequest,
        },
        stream,
      },
    });
  } catch (error) {
    ErrorDialog(
      getLocaleText("mention_stream_mention_preview.unable_to_load_media_item"),
      error
    );
  }
}

function openTveyesPlayerModal(
  tveyesMention: TvEyesMention,
  {
    forceFullBroadcast = false,
    mediaRequest,
  }: Pick<
    OpenTvEyesMentionModalOptions,
    "forceFullBroadcast" | "mediaRequest"
  > = {}
) {
  ModalService.open("TveyesPlayerModal", {
    props: {
      forceFullBroadcast,
      mediaRequest,
      mention: tveyesMention,
    },
  });
}

function fetchBroadcastMentionMediaRequests(
  broadcastMention: TvCaption | RadioClip
): Promise<MediaRequestResource[]> {
  const query = {
    archived: false,
    originKeyword: ["TvEyes", "Kinetiq"],
    sourceId: broadcastMention.id,
    sourceType: upperFirst(camelCase(broadcastMention.type)),
  };

  return fetchCurrentUserMediaRequests(query);
}

async function pollTvEyesMentionMediaRequest(
  tvEyesMention: TvCaption | RadioClip
) {
  const pollIfNeeded = async ({
    pollsSoFar = 0,
    startCheckingStatusOnlyAfterMs = 0,
  } = {}) => {
    const checkStatus =
      pollsSoFar * MEDIA_REQUEST_POLLING_INTERVAL_MS >=
      startCheckingStatusOnlyAfterMs;

    if (pollsSoFar < MEDIA_REQUEST_POLLING_MAX_TIMES) {
      let mediaRequests: MediaRequestResource[];

      try {
        mediaRequests = await fetchBroadcastMentionMediaRequests(tvEyesMention);
      } catch {
        // polls are rescheduled via setTimeout, so a rejection here would go unhandled; stop polling instead
        return;
      }

      const fetchedMediaRequest = mediaRequests.find((request) =>
        isTvEyesMediaRequest(mapFromResource(request))
      );

      const mappedMediaRequest: Nullable<MediaRequest> = fetchedMediaRequest
        ? mapFromResource(fetchedMediaRequest)
        : null;

      // eslint-disable-next-line no-param-reassign
      tvEyesMention.tveyes_media_request = mappedMediaRequest ?? undefined;

      // eslint-disable-next-line no-param-reassign
      tvEyesMention.broadcast_media_request = mediaRequests[0]
        ? mapFromResource(mediaRequests[0])
        : undefined;

      if (fetchedMediaRequest) {
        bus.$emit("media-request-update", fetchedMediaRequest);
      }

      // when we can assume that processing (if any at all) should definitely have started by that time,
      // and it is finished, or no media request at all, nothing more to do
      if (
        checkStatus &&
        (!mappedMediaRequest || isMediaRequestProcessed(mappedMediaRequest))
      )
        return;

      setTimeout(
        () =>
          pollIfNeeded({
            pollsSoFar: pollsSoFar + 1,
            startCheckingStatusOnlyAfterMs,
          }),
        MEDIA_REQUEST_POLLING_INTERVAL_MS
      );
    }
  };

  await pollIfNeeded({
    // in an edge case when user places an order in TvEyes to archive a clip and closes the modal right away,
    // let's make sure we won't miss the first update if it's delayed for some reason
    startCheckingStatusOnlyAfterMs: 20_000,
  });
}

function canReplaceArchivedClip(
  mention: Mention,
  mediaRequest?: Nullable<BasicMediaRequest>
): boolean {
  if (!isTvCaptionOrRadioClip(mention)) return false;

  if (isBroadcastProviderUnsupported(mention)) return false;

  if (!isTvEyesMention(mention)) return false;

  if (!mention.tveyes_editor_url) return false;

  const effectiveMediaRequest =
    mediaRequest || getBroadcastMediaRequest(mention);

  if (!effectiveMediaRequest) return false;

  return (
    !isTveyesExpired(mention) && isMediaRequestCompleted(effectiveMediaRequest)
  );
}

function mapTvCaptionFromResource(
  tvCaptionResource: TvCaptionResource
): TvCaptionFromResource {
  return {
    ...(tvCaptionResource.broadcastProvider !== undefined
      ? { broadcast_provider: tvCaptionResource.broadcastProvider }
      : {}),
    end_time: tvCaptionResource.endTime,
    id: Number(tvCaptionResource.id),
    program_exclusion: tvCaptionResource.programExclusion,
    source: ((sourceResource) => ({
      id: Number(sourceResource.id),
      location: sourceResource.location.name,
      logo_url: sourceResource.logoUrl,
      name: sourceResource.name,
      time_zone: sourceResource.timeZone,
    }))(tvCaptionResource.source),
    start_time: tvCaptionResource.startTime,
    text: tvCaptionResource.text,
    tveyes_editor_url: tvCaptionResource.tveyesEditorUrl,
    tveyes_player_url: tvCaptionResource.tveyesPlayerUrl,
    tveyes_slim_player_url: tvCaptionResource.tveyesSlimPlayerUrl,
    type: tvCaptionResource.type as MentionType,
  };
}

function mapRadioClipFromResource(
  radioClipResource: RadioClipResource
): RadioClipFromResource {
  return {
    audience: radioClipResource.audience,
    ...(radioClipResource.broadcastProvider !== undefined
      ? { broadcast_provider: radioClipResource.broadcastProvider }
      : {}),
    created_at: radioClipResource.createdAt,
    end_time: radioClipResource.endTime,
    id: Number(radioClipResource.id),
    language_code: radioClipResource.languageCode,
    source: ((sourceResource) => ({
      id: Number(sourceResource.id),
      location: sourceResource.location.name,
      logo_url: sourceResource.logoUrl,
      name: sourceResource.name,
      time_zone: sourceResource.timeZone,
    }))(radioClipResource.source),
    start_time: radioClipResource.startTime,
    text: radioClipResource.text,
    tveyes_editor_url: radioClipResource.tveyesEditorUrl,
    tveyes_player_url: radioClipResource.tveyesPlayerUrl,
    tveyes_slim_player_url: radioClipResource.tveyesSlimPlayerUrl,
    type: radioClipResource.type as MentionType,
    ucp_id: radioClipResource.ucpId,
  };
}

export {
  canReplaceArchivedClip,
  fetchBroadcastMentionMediaRequests,
  fetchMention,
  getBroadcastMediaRequest,
  hasArchivedBroadcastContent,
  hasArchivedTvEyesContent,
  hasBroadcastArchiveRequest,
  isTvCaptionOrRadioClip,
  isTvEyesContent,
  mapRadioClipFromResource,
  mapTvCaptionFromResource,
  openTveyesPlayerModal,
  openViewMentionDetailWithKinetiqModal,
  openViewMentionDetailWithTvEyesModal,
};
